import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company } from "../lib/access";
import type { PhoneNumber } from "./Numbers";

interface Config {
  greeting_text: string | null;
  recording_notice: string | null;
  record_calls: boolean;
  strategy: "simultaneous" | "sequential";
  ring_seconds: number;
  no_answer: "voicemail" | "forward" | "hangup";
  forward_to: string | null;
  voicemail_prompt: string | null;
  missed_call_sms: boolean;
  missed_call_text: string | null;
  hours_enabled: boolean;
}
interface Target {
  id: string; user_id: string | null; external_e164: string | null;
  position: number; enabled: boolean;
  profiles: { full_name: string | null } | null;
}
interface Person { user_id: string; profiles: { full_name: string | null } | null }

export function Routing({
  company, number, onBack,
}: { company: Company; number: PhoneNumber; onBack: () => void }) {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [addUser, setAddUser] = useState("");
  const [addExternal, setAddExternal] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, t, p] = await Promise.all([
      supabase.from("number_routing").select("*").eq("phone_number_id", number.id).single(),
      supabase.from("ring_targets")
        .select("id,user_id,external_e164,position,enabled,profiles:user_id(full_name)")
        .eq("phone_number_id", number.id).order("position"),
      supabase.from("memberships").select("user_id,profiles(full_name)")
        .eq("company_id", company.id).eq("status", "active"),
    ]);
    setCfg((c.data as Config) ?? null);
    setTargets((t.data as unknown as Target[]) ?? []);
    setPeople((p.data as unknown as Person[]) ?? []);
  }, [number.id, company.id]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!cfg) return;
    setSaving(true); setMsg(null);
    const { error } = await supabase.from("number_routing").update(cfg)
      .eq("phone_number_id", number.id);
    setSaving(false);
    setMsg(error
      ? (error.message.includes("forward_needs_a_number")
          ? "Choose a number to forward to, or pick a different action."
          : "You do not have permission to change this.")
      : "Saved.");
    void load();
  }

  async function addTarget() {
    setMsg(null);
    const row = {
      company_id: company.id,
      phone_number_id: number.id,
      user_id: addUser || null,
      external_e164: addExternal.trim() || null,
      position: targets.length + 1,
    };
    const { error } = await supabase.from("ring_targets").insert(row);
    if (error) {
      setMsg(error.message.includes("one_kind_of_target")
        ? "Pick a person or type a number — not both."
        : "That did not work. Check the number is in +254… form.");
      return;
    }
    setAddUser(""); setAddExternal("");
    void load();
  }

  if (!cfg) return <p className="muted">Loading…</p>;

  const set = <K extends keyof Config>(k: K, v: Config[K]) => setCfg({ ...cfg, [k]: v });

  return (
    <>
      <header className="page-head">
        <button className="btn ghost small" onClick={onBack}>← All numbers</button>
        <h1>{number.e164}</h1>
        <p className="muted">What happens when somebody calls this number.</p>
      </header>

      <section className="card">
        <h2>Who rings</h2>
        {targets.length === 0 && (
          <p className="status error">
            Nobody rings this number yet. Add at least one person or number, or calls will
            go straight to whatever you have chosen below.
          </p>
        )}
        <ul className="checks">
          {targets.map((t) => (
            <li key={t.id}>
              <label className="check">
                <input type="checkbox" checked={t.enabled}
                  onChange={async (e) => {
                    await supabase.from("ring_targets")
                      .update({ enabled: e.target.checked }).eq("id", t.id);
                    void load();
                  }} />
                {t.profiles?.full_name ?? t.external_e164}
              </label>
              <button className="linkish" onClick={async () => {
                await supabase.from("ring_targets").delete().eq("id", t.id);
                void load();
              }}>remove</button>
            </li>
          ))}
        </ul>

        <div className="two">
          <div>
            <label htmlFor="au">Add a colleague</label>
            <select id="au" value={addUser}
              onChange={(e) => { setAddUser(e.target.value); setAddExternal(""); }}>
              <option value="">—</option>
              {people.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.profiles?.full_name ?? p.user_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ae">Or any phone number</label>
            <input id="ae" value={addExternal} placeholder="+254722000000"
              onChange={(e) => { setAddExternal(e.target.value); setAddUser(""); }} />
          </div>
        </div>
        <button className="btn" onClick={addTarget} disabled={!addUser && !addExternal.trim()}>
          Add to the ring group
        </button>

        <label htmlFor="st">How they ring</label>
        <select id="st" value={cfg.strategy}
          onChange={(e) => set("strategy", e.target.value as Config["strategy"])}>
          <option value="simultaneous">All at once</option>
          <option value="sequential">One after another, in order</option>
        </select>

        <label htmlFor="rs">Ring for (seconds)</label>
        <input id="rs" type="number" min={5} max={120} value={cfg.ring_seconds}
          onChange={(e) => set("ring_seconds", Number(e.target.value))} />
      </section>

      <section className="card">
        <h2>What the caller hears</h2>
        <label htmlFor="gt">Greeting</label>
        <input id="gt" value={cfg.greeting_text ?? ""} placeholder="Thank you for calling…"
          onChange={(e) => set("greeting_text", e.target.value)} />

        <label className="check">
          <input type="checkbox" checked={cfg.record_calls}
            onChange={(e) => set("record_calls", e.target.checked)} />
          Record calls on this number
        </label>

        <label htmlFor="rn">Recording notice</label>
        <input id="rn" value={cfg.recording_notice ?? ""}
          onChange={(e) => set("recording_notice", e.target.value)} />
        <p className="muted small">
          Played before recording starts. Announcing it is your legal duty in most places,
          so the wording is yours to set.
        </p>
      </section>

      <section className="card">
        <h2>If nobody answers</h2>
        <label htmlFor="na">Then</label>
        <select id="na" value={cfg.no_answer}
          onChange={(e) => set("no_answer", e.target.value as Config["no_answer"])}>
          <option value="voicemail">Take a voicemail</option>
          <option value="forward">Forward to another number</option>
          <option value="hangup">Say goodbye and hang up</option>
        </select>

        {cfg.no_answer === "forward" && (
          <>
            <label htmlFor="ft">Forward to</label>
            <input id="ft" value={cfg.forward_to ?? ""} placeholder="+254733000000"
              onChange={(e) => set("forward_to", e.target.value)} />
          </>
        )}
        {cfg.no_answer === "voicemail" && (
          <>
            <label htmlFor="vp">Voicemail prompt</label>
            <input id="vp" value={cfg.voicemail_prompt ?? ""}
              onChange={(e) => set("voicemail_prompt", e.target.value)} />
          </>
        )}

        <label className="check">
          <input type="checkbox" checked={cfg.missed_call_sms}
            onChange={(e) => set("missed_call_sms", e.target.checked)} />
          Text the caller back when we miss them
        </label>
        {cfg.missed_call_sms && (
          <input aria-label="Missed call text" value={cfg.missed_call_text ?? ""}
            onChange={(e) => set("missed_call_text", e.target.value)} />
        )}
      </section>

      <button className="btn" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save settings"}
      </button>
      {msg && <p className="status">{msg}</p>}
    </>
  );
}
